import { API_URL } from 'Constants'
import { useUI } from 'ctx/UIContext'
import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { getHeaders } from 'lib/apiHelpers'
import { generateRandomNumberInRange, lsget, lsset } from 'lib/misc'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const browser = typeof window !== 'undefined'

// The atom to hold the value goes here
const themeAtom = atomWithStorage(
  'theme',
  browser && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
)

// global folder name
const folderAtom = atomWithStorage('folder', '')

type Submission = any
type User = any

const submissionsAtom = atom<Submission[]>([])
const affiliateDataAtom = atom<any>([])
const userAtom = atomWithStorage<User>('current_user', {})
const notificationsAtom = atom<any[]>([])

/** Sitewide theme */
export function useTheme(): [string, any] {
  const [theme, setTheme] = useAtom(themeAtom)

  useEffect(() => {
    if (!browser) return

    document.body.classList.remove('light', 'dark')
    document.body.classList.add(theme)
  }, [theme])

  return [theme, setTheme]
}
export function useTitle(): [string, any, any] {
  const [title, setTitle] = useAtom(folderAtom)
  const titlePre = generateRandomNumberInRange()

  const updateTitle = (val: string) => {
    const newTitle = `${val
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .replaceAll(' ', '_')}_${titlePre.toString()}`
    setTitle(newTitle)
  }

  useEffect(() => {
    setTitle('')
  }, [])

  return [title, updateTitle, setTitle]
}

const APIRequest = async (url: string, options: RequestInit) => {
  return await fetch(`${url}`, options)
    .then((res) => {
      if (!res.ok) {
        throw new Error(res.status.toString())
      }
      return res.json()
    })
    .catch((err) => {
      if (err && err.message && err.message?.toLowerCase() === '403') {
        localStorage.clear()
        window?.location?.reload()
      }
    })
}

export function useSubmissions(): {
  getSubmissions: () => Promise<any>
} {
  const [submissions, setSubmissions] = useAtom(submissionsAtom)
  const { setLoading } = useUI()
  const navigate = useNavigate()
  const { user } = useUser()

  const getSubmissions = async () => {
    const headers = getHeaders()

    if (!headers) return
    if (!user || !user.user_id) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }
    return await fetch(
      `${API_URL}submission?user_id=${user.user_id}`,
      requestOptions
    )
      .then((res) => res.json())
      .catch((err) => {
        console.error(err)
        navigate('/my-submissions')
      })
      .finally(() => {
        // setLoading(false)
      })
  }

  return {
    getSubmissions,
  }
}

export function useUser() {
  const navigate = useNavigate()
  const [user, setUser] = useAtom(userAtom)

  const [affiliateData, setAffiliateData] = useAtom(affiliateDataAtom)

  const { isLoading, setLoading } = useUI()

  const getUser = async (email: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await APIRequest(`${API_URL}user?user=${email}`, requestOptions)
  }

  const getAffiliate = async (agent_id?: string, email?: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    const _url = `${
      agent_id && agent_id !== ''
        ? `agent_id=${agent_id}`
        : email && email !== ''
        ? `email=${email}`
        : ''
    }`

    return await APIRequest(`${API_URL}afilliate?${_url}`, requestOptions)
  }

  const updateUser = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = JSON.stringify({
      user_id: user.user_id,
      firstname: data.firstName,
      lastname: data.lastName,
      email: data.email,
      paypal: data.paypal,
      instagram_handle: data.instagramHandle,
    })

    const requestOptions: RequestInit = {
      method: 'PUT',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}user`, requestOptions)
  }

  const updateAffiliate = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    // if (!user && !user.user_id) return

    const raw = JSON.stringify({
      agent_id: user.agent_id,
      name: data.name,
      email: data.email,
      paypal_email: data.paypal_email,
      phone: data.phone,
      address: data.address,
      emailenable: data.notifications?.toString(),
    })

    const requestOptions: RequestInit = {
      method: 'PUT',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}afilliate`, requestOptions)
  }

  const updateAffiliateData = async () => {
    if (user && 'agent_id' in user) {
      await getAffiliate(user.agent_id).then((res) => {
        if (res) {
          setAffiliateData(res)
        }
      })
    }
  }

  return {
    user,
    getUser,
    getAffiliate,
    setUser,
    updateUser,
    affiliateData,
    setAffiliateData,
    updateAffiliate,
    updateAffiliateData,
  }
}

export function useAnalytics() {
  const getAnalytics = async (agent_id: string, quarter: string, year: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await fetch(
      `${API_URL}accounting-earnings?agent_id=${agent_id}&quarter=${quarter}&year=${year}`,
      requestOptions
    )
      .then((response) => response.json())
      .catch((err) => console.log(err))
  }
  const getDuePayments = async (user_id: string, quarter: string, year: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await fetch(
      `${API_URL}accounting-payment?agent_id=${user_id}&quarter=${quarter}&year=${year}`,
      requestOptions
    )
      .then((response) => response.json())
      .catch((err) => console.log(err))
  }

  const requestDuePayments = async (payment_id: string) => {
    const headers = getHeaders()
    if (!headers) return

    const raw = new FormData()
    raw.append('payment_id', payment_id)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
      body: raw,
    }

    return await fetch(`${API_URL}accounting-payment`, requestOptions)
      .then((response) => response.json())
      .catch((err) => console.log(err))
  }
  return { getAnalytics, getDuePayments, requestDuePayments }
}

export function useNotifications() {
  const { user } = useUser()

  const [notifications, setNotifications] = useAtom(notificationsAtom)

  const getNotifications = async (user_id: string) => {
    const headers = getHeaders()

    if (!headers) return

    const options: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    const url = `${API_URL}notification?user_id=${user_id}`

    return await fetch(url, options)
      .then((response) => response.json())
      .catch((err) => console.log(err))
  }

  const markAsRead = async (notification_id: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('notification_id', notification_id)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}notification`
    return await fetch(`${url}`, options)
      .then((res) => res.json())
      .catch((err) => {
        console.error(err)
      })
  }

  const unsubscribe = async (user_id: string, category: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('user_id', user_id)
    raw.append('category', category)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}unsubscribe`
    return await fetch(`${url}`, options)
      .then((res) => res.json())
      .catch((err) => {
        console.error(err)
      })
  }

  const unsubscribeNewsletter = async (email: string, category: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('email', email)
    raw.append('category', category)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}unsubscribe`
    return await fetch(`${url}`, options)
      .then((res) => res.json())
      .catch((err) => {
        console.error(err)
      })
  }

  const updateNotifications = async () => {
    if (!user) return
    // console.log(user)
    await getNotifications(user.agent_id).then((res) => {
      if (res && 'list' in res) {
        setNotifications(res.list)
      }
    })
  }

  useEffect(() => {
    updateNotifications()
  }, [user])

  return {
    notifications,
    getNotifications,
    updateNotifications,
    markAsRead,
    unsubscribe,
    unsubscribeNewsletter,
  }
}
